import React, { useState, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { getMapOptions } from '../../../utils/helpers/getMapOptions';
import useScreenSize from '../../../hooks/useScreenSize';
import { MapProps } from './types';
import { MapContainer, Pin, ShadowDot } from './styled';
import { useGoogleMaps } from '../../../providers/GoogleMapsProvider';

export const Map: React.FC<MapProps> = () => {
  const { mapData } = useGoogleMaps();
  const { isWebView } = useBridgeState();
  const { isSmall } = useScreenSize();

  const isMarkerDraggable = mapData?.isMarkerDraggable ?? true;
  const [isDragging, setIsDragging] = useState(false);

  const { mapOptions, mapStyle, mapHeight } = useMemo(
    () => getMapOptions(isSmall, isWebView, isMarkerDraggable),
    [isSmall, isWebView, isMarkerDraggable]
  );

  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const handleLoad = useCallback(
    (map: google.maps.Map) => {
      if (!isMarkerDraggable) return;

      const dragStartListener = map.addListener('dragstart', () =>
        setIsDragging(true)
      );
      const dragEndListener = map.addListener('dragend', () => {
        setIsDragging(false);
        const mapCenter = map.getCenter();
        if (mapCenter && mapData?.setCoordinates) {
          mapData.setCoordinates({
            lat: mapCenter.lat(),
            lng: mapCenter.lng()
          });
        }
      });

      listenersRef.current = [dragStartListener, dragEndListener];
    },
    [mapData?.setCoordinates]
  );

  const handleUnmount = useCallback(() => {
    if (listenersRef?.current?.length) {
      listenersRef.current.forEach(l => l.remove());
      listenersRef.current = [];
    }
  }, []);

  return (
    <MapContainer $height={mapHeight}>
      <GoogleMap
        zoom={14}
        options={mapOptions}
        center={mapData.coordinates}
        mapContainerStyle={mapStyle}
        onLoad={handleLoad}
        onUnmount={handleUnmount}
      >
        {!isMarkerDraggable && mapData.coordinates && (
          <Marker
            draggable={isMarkerDraggable}
            position={mapData.coordinates}
            icon="/static/images/new_ad/icons/icon-map-pin.png"
          />
        )}
      </GoogleMap>
      {isMarkerDraggable &&
        (mapData.pinSelected || mapData.isOpen) &&
        mapData.coordinates && (
          <>
            <ShadowDot $dragging={isDragging} />
            <Pin $dragging={isDragging}>
              <img
                src="/static/images/new_ad/icons/icon-map-pin.png"
                alt="pin"
              />
            </Pin>
          </>
        )}
    </MapContainer>
  );
};
